import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { examActions, questionTypeActions } from '../../redux/actions';
import { AppDispatch, RootState } from '../../store';

// ==================== STYLED COMPONENTS ====================
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  flex-shrink: 0;
  min-height: 40px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  color: #4b5563;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }
`;

const ExamTitle = styled.input`
  padding: 6px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  width: 200px;
  min-width: 150px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CreateButton = styled.button`
  padding: 6px 16px;
  background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormField = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const FormFieldLabel = styled.label`
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const FormFieldInput = styled.input<{ $width?: string }>`
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 12px;
  flex: ${props => props.$width ? '0' : '1'};
  width: ${props => props.$width || 'auto'};
  min-width: 50px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const FormFieldSelect = styled.select`
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 12px;
  flex: 1;
  min-width: 80px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 16px;
  padding: 0;
  max-height: 90vh;
`;

// ==================== SPLIT SCREEN LAYOUT ====================
const SplitContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 5fr;
  gap: 8px;
  flex: 1;
  overflow: hidden;
  padding: 12px;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 6px 10px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

// ==================== TEXT EDITOR (RIGHT) ====================
const TextEditorWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0 0 8px 0;
`;

// Helper function to calculate display lines
// For each hard line (separated by \n), calculate how many visual lines it wraps into
// Return: positive number for line start, 0 for wrapped visual lines
const calculateDisplayLines = (text: string, containerWidth: number): number[] => {
  if (!text || containerWidth <= 0) {
    return text.split('\n').map((_, i) => i + 1);
  }
  
  const lines = text.split('\n');
  const displayLines: number[] = [];
  
  // Create canvas to measure text width accurately
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return lines.map((_, i) => i + 1);
  }
  
  // Set font to match textarea (13px Monaco monospace)
  ctx.font = "13px 'Monaco', 'Menlo', 'Ubuntu Mono', monospace";
  
  // Available width for text (subtract padding 24px = 12px on each side)
  const availableWidth = containerWidth - 24;
  
  if (availableWidth <= 0) {
    return lines.map((_, i) => i + 1);
  }
  
  lines.forEach((line, lineIndex) => {
    const lineNumber = lineIndex + 1;
    
    // Show line number at start of hard line
    displayLines.push(lineNumber);
    
    if (line.length === 0) return;
    
    // Expand tabs to spaces (tab-size: 4) to match textarea rendering
    const expandedLine = line.replace(/\t/g, '    ');
    
    // Measure actual pixel width of this line
    const lineWidth = ctx.measureText(expandedLine).width;
    
    // Calculate how many visual lines this wraps into
    if (lineWidth > availableWidth) {
      const visualLineCount = Math.ceil(lineWidth / availableWidth);
      const wrappedVisualLines = visualLineCount - 1;
      
      // Add 0 for each wrapped visual line
      for (let i = 0; i < wrappedVisualLines; i++) {
        displayLines.push(0);
      }
    }
  });
  
  return displayLines;
};

const LineNumbersColumn = styled.div<{ $lineCount: number }>`
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  padding: 0;
  width: ${props => Math.max(30, (Math.ceil(Math.log10(Math.max(props.$lineCount, 1))) + 1) * 8 + 6)}px;
  flex-shrink: 0;
  overflow: hidden;
  user-select: none;
`;

const LineNumber = styled.div<{ $isActive: boolean }>`
  padding: 0 6px 0 0;
  font-size: 13px;
  line-height: 1.6;
  height: 20.8px;
  text-align: right;
  color: ${props => props.$isActive ? '#007bff' : '#9ca3af'};
  background: ${props => props.$isActive ? '#eff6ff' : 'transparent'};
  border-left: ${props => props.$isActive ? '2px solid #007bff' : 'none'};
  padding-left: ${props => props.$isActive ? '4px' : '6px'};
  transition: all 0.15s ease;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  cursor: pointer;
`;

const TextEditorInput = styled.textarea`
  flex: 1;
  padding: 0;
  border: none;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  resize: none;
  background: white;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  width: 100%;
  white-space: pre-wrap;
  tab-size: 4;

  &:focus {
    outline: none;
  }
`;

// ==================== PREVIEW (LEFT) ====================
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionPreview = styled.div`
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f0f4f8;
    border-color: #007bff;
  }
`;

const QuestionNumber = styled.div`
  font-weight: 600;
  color: #007bff;
  margin-bottom: 6px;
  font-size: 12px;
`;

const QuestionText = styled.div`
  font-size: 13px;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.5;
  font-weight: 500;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Option = styled.div`
  font-size: 12px;
  color: #4b5563;
  padding: 4px 0;
  line-height: 1.4;
`;

const TypeBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  padding: 3px 8px;
  background: #eff6ff;
  color: #007bff;
  border-radius: 4px;
  font-weight: 600;
  margin-left: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  text-align: center;
  font-size: 14px;

  p {
    margin: 0;
  }
`;

// ==================== TYPE DEFINITIONS ====================
interface ParsedQuestion {
  number: number;
  text: string;
  options?: string[];
  answer?: string;
  typeId: number;
  typeName: string;
}

// ==================== MAIN COMPONENT ====================
const CreateExamPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const questionTypes = useSelector((state: RootState) => (state.questionTypes as any)?.questionTypeList || []);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 60,
    status: 'draft' as 'draft' | 'published',
  });

  // Editor state
  const [editorText, setEditorText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoveredLineNumber, setHoveredLineNumber] = useState<number | null>(null);
  const [editorWidth, setEditorWidth] = useState(0);
  const editorRef = React.useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = React.useRef<HTMLDivElement>(null);
  const textEditorWrapperRef = React.useRef<HTMLDivElement>(null);

  // Load question types on mount
  useEffect(() => {
    if (questionTypes.length === 0) {
      dispatch(questionTypeActions.getAll());
    }
  }, [dispatch, questionTypes.length]);

  // Measure editor width for wrap calculation
  useEffect(() => {
    const measureWidth = () => {
      if (editorRef.current) {
        setEditorWidth(editorRef.current.clientWidth);
      }
    };
    
    measureWidth();
    const resizeObserver = new ResizeObserver(measureWidth);
    if (editorRef.current) {
      resizeObserver.observe(editorRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, []);

  // Adjust textarea height to fit content for proper scrolling
  // Also re-measure width to ensure line wrapping calculations are accurate
  useEffect(() => {
    if (editorRef.current) {
      // Reset height to auto to get scrollHeight
      editorRef.current.style.height = 'auto';
      // Set height to scrollHeight so all content is visible
      editorRef.current.style.height = editorRef.current.scrollHeight + 'px';
      
      // Re-measure width after content changes (always update to trigger recalculation)
      const width = editorRef.current.clientWidth;
      if (width > 0) {
        setEditorWidth(width);
      }
    }
  }, [editorText]);


  // ==================== TEXT PARSING LOGIC ====================
  const parseQuestions = (text: string): ParsedQuestion[] => {
    if (!text.trim()) return [];

    const questions: ParsedQuestion[] = [];
    
    // Split by numbered lines (1., 2., etc.)
    const questionBlocks = text.split(/\n(?=\d+\.)/);

    questionBlocks.forEach((block, index) => {
      const lines = block.trim().split('\n').filter(line => line.trim());
      if (lines.length === 0) return;

      // First line is the question number and text
      const firstLine = lines[0];
      const numberMatch = firstLine.match(/^(\d+)\.\s*(.*)/);
      
      if (!numberMatch) return;

      const questionNumber = parseInt(numberMatch[1]);
      let questionText = numberMatch[2];
      let typeId = 1; // Default to multiple choice
      let typeName = 'Trắc nghiệm';
      let options: string[] = [];
      let answer: string | undefined;

      // Parse remaining lines for options and answer
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();

        // Detect option (A), B), C), D) pattern
        if (/^[A-Z]\)\s*/.test(line)) {
          const optionText = line.replace(/^[A-Z]\)\s*/, '');
          options.push(optionText);
        }
        // Detect answer
        else if (/^Đáp án:/i.test(line)) {
          answer = line.replace(/^Đáp án:\s*/i, '').trim();
          typeId = 2; // Điền từ/tự luận
          typeName = 'Điền từ';
        }
        // If no options pattern, it's fill-in-the-blank
        else if (line && options.length === 0 && !answer) {
          questionText += ` ${line}`;
        }
      }

      // Auto-detect typeId based on structure
      if (options.length >= 2 && options.length <= 4) {
        typeId = 1; // Trắc nghiệm
        typeName = 'Trắc nghiệm';
      } else if (options.length > 4) {
        typeId = 3; // Có thể là matching
        typeName = 'Ghép cặp';
      }

      questions.push({
        number: questionNumber,
        text: questionText.trim(),
        options: options.length > 0 ? options : undefined,
        answer,
        typeId,
        typeName,
      });
    });

    return questions;
  };

  const parsedQuestions = useMemo(() => parseQuestions(editorText), [editorText]);

  // ==================== EVENT HANDLERS ====================
  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateExam = async () => {
    if (!formData.title.trim()) {
      alert('Vui lòng nhập tên đề thi!');
      return;
    }

    if (!formData.duration || formData.duration <= 0) {
      alert('Vui lòng nhập thời gian làm bài hợp lệ!');
      return;
    }

    setSubmitting(true);

    try {
      // Create exam first (empty exam)
      const createdExam = await dispatch(examActions.createExam({
        title: formData.title,
        description: formData.description,
        duration: formData.duration,
        status: formData.status,
      }));

      alert('✅ Tạo đề thi thành công! Bạn có thể thêm câu hỏi sau.');
      navigate('/exams-manage');
    } catch (error) {
      alert('❌ Lỗi khi tạo đề thi: ' + error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (editorText.trim()) {
      if (window.confirm('Bạn chưa lưu các câu hỏi. Bạn có chắc muốn quay lại?')) {
        navigate('/exams-manage');
      }
    } else {
      navigate('/exams-manage');
    }
  };

  const handleTextEditorClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    // Count \n characters before cursor to get line number
    const lineNumber = text.substring(0, cursorPos).split('\n').length;
    setHoveredLineNumber(lineNumber);
  };

  const handleTextEditorKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Shift+Enter as a line break that should increase STT
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const cursorPos = textarea.selectionStart;
      const textBefore = textarea.value.substring(0, cursorPos);
      const textAfter = textarea.value.substring(cursorPos);
      
      // Insert marker + newline for Shift+Enter
      const newText = textBefore + '\n' + textAfter;
      setEditorText(newText);
      
      // Update cursor position after state update
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.selectionStart = editorRef.current.selectionEnd = cursorPos + 1;
        }
      }, 0);
    }
  };

  const handleTextEditorKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    // Count \n characters before cursor to get line number
    const lineNumber = text.substring(0, cursorPos).split('\n').length;
    setHoveredLineNumber(lineNumber);
  };

  const handleTextEditorWrapperScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const wrapper = e.currentTarget;
    if (lineNumbersRef.current) {
      requestAnimationFrame(() => {
        if (lineNumbersRef.current) {
          lineNumbersRef.current.scrollTop = wrapper.scrollTop;
        }
      });
    }
  };

  const totalLines = editorText.split('\n').length;

  // Calculate line numbers - account for soft wraps
  const displayLines = useMemo(() => calculateDisplayLines(editorText, editorWidth), [editorText, editorWidth]);

  return (
    <Container>
      {/* HEADER */}
      <Header>
        <BackButton onClick={handleBack}>← Quay lại</BackButton>
        <ExamTitle
          type="text"
          placeholder="Tên đề thi *"
          value={formData.title}
          onChange={(e) => handleFormChange('title', e.target.value)}
        />

        <FormField>
          <FormFieldLabel>Thời gian</FormFieldLabel>
          <FormFieldInput
            type="number"
            min="1"
            $width="50px"
            value={formData.duration}
            onChange={(e) => handleFormChange('duration', parseInt(e.target.value))}
          />
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>phút</span>
        </FormField>

        <FormField>
          <FormFieldLabel>Trạng thái</FormFieldLabel>
          <FormFieldSelect
            value={formData.status}
            onChange={(e) => handleFormChange('status', e.target.value)}
          >
            <option value="draft">Bản nháp</option>
            <option value="published">Xuất bản</option>
          </FormFieldSelect>
        </FormField>

        <FormField>
          <FormFieldLabel>Mô tả</FormFieldLabel>
          <FormFieldInput
            type="text"
            placeholder="Mô tả..."
            value={formData.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
          />
        </FormField>

        <CreateButton disabled={submitting} onClick={handleCreateExam}>
          {submitting ? '⏳ Tạo...' : '✓ Tạo Đề'}
        </CreateButton>
      </Header>

      {/* SPLIT SCREEN LAYOUT */}
      <Content>
        <SplitContainer>
          {/* LEFT: PREVIEW */}
          <Panel>
            <PanelHeader>📄 Xem Trước ({parsedQuestions.length} câu)</PanelHeader>
            <PanelContent style={{ padding: '16px' }}>
              {parsedQuestions.length === 0 ? (
                <EmptyState>
                  <p>👈 Nhập câu hỏi bên phải để xem trước</p>
                </EmptyState>
              ) : (
                <PreviewContainer>
                  {parsedQuestions.map((q) => (
                    <QuestionPreview key={q.number}>
                      <QuestionNumber>
                        Câu {q.number} <TypeBadge>{q.typeName}</TypeBadge>
                      </QuestionNumber>
                      <QuestionText>{q.text}</QuestionText>
                      {q.options && (
                        <OptionsList>
                          {q.options.map((opt, idx) => (
                            <Option key={idx}>
                              {String.fromCharCode(65 + idx)}) {opt}
                            </Option>
                          ))}
                        </OptionsList>
                      )}
                      {q.answer && (
                        <Option style={{ color: '#10b981', fontWeight: 500, marginTop: '4px' }}>
                          ✓ Đáp án: {q.answer}
                        </Option>
                      )}
                    </QuestionPreview>
                  ))}
                </PreviewContainer>
              )}
            </PanelContent>
          </Panel>

          {/* RIGHT: TEXT EDITOR */}
          <Panel>
            <PanelHeader>✍️ Nhập Câu Hỏi (Copy/Paste từ Azota)</PanelHeader>
            <PanelContent style={{ padding: 0 }}>
              <TextEditorWrapper ref={textEditorWrapperRef} onScroll={handleTextEditorWrapperScroll}>
                <LineNumbersColumn ref={lineNumbersRef} $lineCount={displayLines.length}>
                  {displayLines.map((lineNum, idx) => (
                    <LineNumber key={idx} $isActive={lineNum > 0 && hoveredLineNumber === lineNum}>
                      {lineNum > 0 ? lineNum : ''}
                    </LineNumber>
                  ))}
                </LineNumbersColumn>
                <TextEditorInput
                  ref={editorRef}
                  placeholder={`Ví dụ:
1. Đây là câu hỏi trắc nghiệm?
A) Đáp án A
B) Đáp án B
C) Đáp án C
D) Đáp án D

2. _____ là gì?
Đáp án: từ điền

3. Nối cột A với cột B
A) Mục A1
B) Mục A2
C) Mục B1
D) Mục B2`}
                  value={editorText}
                  onChange={(e) => {
                    // Normalize text: replace various newline characters with just \n
                    // This prevents hidden line breaks from copy/paste
                    const normalizedText = e.target.value
                      .replace(/\r\n/g, '\n')  // Windows line ending
                      .replace(/\r/g, '\n');   // Old Mac line ending
                    setEditorText(normalizedText);
                  }}
                  onClick={handleTextEditorClick}
                  onKeyDown={handleTextEditorKeyDown}
                  onKeyUp={handleTextEditorKeyUp}
                  style={{ padding: '0 12px' }}
                />
              </TextEditorWrapper>
            </PanelContent>
          </Panel>
        </SplitContainer>
      </Content>
    </Container>
  );
};

export default CreateExamPage;
